import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { BudgetRevision } from '@/types/budget.types';
import { formatCurrency } from '@/lib/budget-utils';
import { Clock, User, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { getStatusColor } from '@/lib/status-colors';

interface BudgetRevisionTimelineProps {
  revisions: BudgetRevision[];
}

export function BudgetRevisionTimeline({ revisions }: BudgetRevisionTimelineProps) {
  const getRevisionStatusColor = (status: string) => {
    // Map revision status to semantic status types
    switch (status) {
      case 'current':
        return 'bg-green-100 dark:bg-green-900'; // Success
      case 'baseline':
        return 'bg-blue-100 dark:bg-blue-900'; // Progress
      case 'draft':
        return 'bg-amber-100 dark:bg-amber-900'; // Warning
      default:
        return 'bg-gray-100 dark:bg-gray-900'; // Pending
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'current':
        return 'Current';
      case 'baseline':
        return 'Baseline';
      case 'draft':
        return 'Draft';
      case 'superseded':
        return 'Superseded';
      default:
        return status;
    }
  };

  if (revisions.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        No revisions to display
      </div>
    );
  }

  return (
    <div className="space-y-6 py-4">
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

        {/* Timeline items */}
        <div className="space-y-6">
          {revisions.map((revision, index) => (
            <div key={revision.revisionId || revision.id} className="relative pl-14">
              {/* Timeline dot */}
              <div
                className={`absolute left-4 top-2 h-5 w-5 rounded-full border-4 border-background ${getRevisionStatusColor(
                  revision.status
                )}`}
              />

              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">
                            {revision.revisionNumber}
                          </h3>
                          <Badge className={getStatusColor(revision.status)}>
                            {getStatusLabel(revision.status)}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {new Date(revision.createdAt || revision.date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {revision.createdBy || revision.changedByName}
                          </div>
                        </div>
                      </div>

                      {/* Budget summary */}
                      <div className="text-right space-y-1">
                        <div className="text-sm text-muted-foreground">Budget</div>
                        <div className="font-semibold">
                          {formatCurrency(revision.budgetData?.totalCost || 0)}
                        </div>
                      </div>
                    </div>

                    {/* Reason */}
                    {revision.reason && (
                      <div className="flex items-start gap-2 p-3 bg-muted rounded-lg">
                        <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                        <div className="text-sm">{revision.reason}</div>
                      </div>
                    )}

                    {/* Changes */}
                    {revision.changes && (
                      <div className="grid grid-cols-2 gap-4">
                        {revision.changes.totalCostChange !== 0 && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                            {revision.changes.totalCostChange > 0 ? (
                              <TrendingUp className="h-4 w-4 text-red-600" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-green-600" />
                            )}
                            <div>
                              <div className="text-xs text-muted-foreground">Cost Change</div>
                              <div
                                className={`font-semibold ${
                                  revision.changes.totalCostChange > 0
                                    ? 'text-red-600'
                                    : 'text-green-600'
                                }`}
                              >
                                {revision.changes.totalCostChange > 0 ? '+' : ''}
                                {formatCurrency(revision.changes.totalCostChange)}
                              </div>
                            </div>
                          </div>
                        )}

                        {revision.changes.totalRevenueChange !== 0 && (
                          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
                            {revision.changes.totalRevenueChange > 0 ? (
                              <TrendingUp className="h-4 w-4 text-green-600" />
                            ) : (
                              <TrendingDown className="h-4 w-4 text-red-600" />
                            )}
                            <div>
                              <div className="text-xs text-muted-foreground">Revenue Change</div>
                              <div
                                className={`font-semibold ${
                                  revision.changes.totalRevenueChange > 0
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {revision.changes.totalRevenueChange > 0 ? '+' : ''}
                                {formatCurrency(revision.changes.totalRevenueChange)}
                              </div>
                            </div>
                          </div>
                        )}

                        {revision.changes.categoriesChanged && revision.changes.categoriesChanged.length > 0 && (
                          <div className="col-span-2 p-3 rounded-lg bg-muted">
                            <div className="text-xs text-muted-foreground mb-2">
                              Categories Changed
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {revision.changes.categoriesChanged.map((category) => (
                                <Badge key={category} variant="outline">
                                  {category}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Approval info */}
                    {revision.approvedBy && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          Approved
                        </Badge>
                        <span>by {revision.approvedBy}</span>
                        {revision.approvedAt && (
                          <span>
                            on{' '}
                            {new Date(revision.approvedAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Time since previous revision */}
                    {index < revisions.length - 1 && (
                      <div className="text-xs text-muted-foreground pt-2 border-t">
                        {getDaysSince(revisions[index + 1].createdAt || revisions[index + 1].date, revision.createdAt || revision.date)} days since previous revision
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>

      {/* Summary footer */}
      <Card className="bg-muted">
        <CardContent className="pt-6">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold">{revisions.length}</div>
              <div className="text-sm text-muted-foreground">Total Revisions</div>
            </div>
            <div>
              <div className="text-2xl font-bold">
                {revisions.filter((r) => r.status === 'baseline').length}
              </div>
              <div className="text-sm text-muted-foreground">Baselines</div>
            </div>
            <div>
              <div className="text-2xl font-bold">
                {getDaysSince(
                  revisions[revisions.length - 1].createdAt || revisions[revisions.length - 1].date,
                  revisions[0].createdAt || revisions[0].date
                )}
              </div>
              <div className="text-sm text-muted-foreground">Days Since First</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function getDaysSince(startDate: string, endDate: string): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}
